/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

export interface PanelDisclaimerProps {
	/**
	 * The content of the disclaimer bar. Consumers are responsible for
	 * the internal layout and alignment of children.
	 */
	children: ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code,
	 * serving as a hook for automated tests.
	 */
	testId?: string;
}

const styles = cssMap({
	disclaimer: {
		paddingBlock: token('space.150'),
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		backgroundColor: token('elevation.surface.sunken'),
		alignSelf: 'stretch',
		font: token('font.body'),
		color: token('color.text.subtle'),
	},
});

/**
 * `PanelDisclaimer` is an optional compositional component passed to the
 * `PanelFooter` disclaimer slot. It provides a styled disclaimer bar with consistent
 * background and spacing. Consumers control the layout of children.
 *
 * @example
 * <PanelContainer>
 *   <PanelFooter
 *     disclaimer={
 *       <PanelDisclaimer>
 *         <span>Powered by Rovo AI. Verify results.</span>
 *       </PanelDisclaimer>
 *     }
 *   >
 *     ...
 *   </PanelFooter>
 * </PanelContainer>
 */
export function PanelDisclaimer({ children, testId }: PanelDisclaimerProps): JSX.Element {
	return (
		<Box testId={testId} xcss={styles.disclaimer}>
			{children}
		</Box>
	);
}
